import React, { useMemo } from "react";
import PropTypes from "prop-types";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import ReplayIcon from "@mui/icons-material/Replay";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

const CASE_META = {
  undelivered: { label: "Undelivered", color: "#94a3b8" },
  case_item: { label: "Case INR", color: "#06b6d4" },
  case_des: { label: "Case NAD", color: "#8b5cf6" },
  fraudulent: { label: "Fraudulent", color: "#f59e0b" },
  case_other: { label: "Case Khác", color: "#64748b" },
};

const CASE_KEYS = Object.keys(CASE_META);
const TYPE_TABLE_KEYS = ["case_item", "fraudulent", "case_des", "case_other"];
const GATEWAY_META = [
  { key: "paypal", label: "Paypal" },
  { key: "stripe", label: "Stripe" },
  { key: "airwallex", label: "Airwallex" },
];

const CARD_SX = {
  height: "100%",
  border: "1px solid #e9ecef",
  boxShadow: "none",
  borderRadius: 2,
};

const TABLE_WRAP = {
  width: "100%",
  overflowX: "auto",
  border: "1px solid #e0e0e0",
  borderRadius: "4px",
};

const TH = {
  fontWeight: 700,
  padding: "6px 8px",
  textAlign: "right",
  borderBottom: "2px solid #e0e0e0",
  whiteSpace: "nowrap",
  fontSize: 12,
};

const TD = {
  padding: "6px 8px",
  textAlign: "right",
  borderBottom: "1px solid #eee",
  fontSize: 12,
};

function emptyType() {
  return CASE_KEYS.reduce((acc, key) => {
    acc[key] = { count: 0, percent: 0 };
    return acc;
  }, {});
}

function normalizeDispute(dispute) {
  if (!dispute || typeof dispute !== "object") {
    return {
      total: 0,
      totalOrders: 0,
      rate: 0,
      byType: emptyType(),
      byStore: {},
      byGateway: {
        paypal: { ...emptyType(), total: 0 },
        stripe: { ...emptyType(), total: 0 },
        airwallex: { ...emptyType(), total: 0 },
      },
      cancelledNew: { count: 0, percent: 0 },
      refundedShipped: { count: 0, percent: 0 },
    };
  }
  return {
    total: Number(dispute.total) || 0,
    totalOrders: Number(dispute.totalOrders) || 0,
    rate: Number(dispute.rate) || 0,
    byType: { ...emptyType(), ...(dispute.byType || {}) },
    byStore: dispute.byStore || {},
    byGateway: {
      paypal: { ...emptyType(), total: 0, ...(dispute.byGateway?.paypal || {}) },
      stripe: { ...emptyType(), total: 0, ...(dispute.byGateway?.stripe || {}) },
      airwallex: { ...emptyType(), total: 0, ...(dispute.byGateway?.airwallex || {}) },
    },
    cancelledNew: {
      count: Number(dispute.cancelledNew?.count) || 0,
      percent: Number(dispute.cancelledNew?.percent) || 0,
    },
    refundedShipped: {
      count: Number(dispute.refundedShipped?.count) || 0,
      percent: Number(dispute.refundedShipped?.percent) || 0,
    },
  };
}

function formatNumber(num) {
  return Number(num || 0).toLocaleString("en-US");
}

function formatPct(num) {
  const n = Number(num);
  if (!Number.isFinite(n)) return "0%";
  return `${n.toFixed(2)}%`;
}

function countOf(byType, key) {
  return Number(byType?.[key]?.count) || 0;
}

function pctOf(byType, key) {
  return Number(byType?.[key]?.percent) || 0;
}

function CompactStatCard({ label, value, hint, color, icon, background }) {
  return (
    <Card sx={{ ...CARD_SX, background }}>
      <CardContent sx={{ py: 1.5, px: 1.5, "&:last-child": { pb: 1.5 } }}>
        <MDBox sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1 }}>
          <MDBox sx={{ minWidth: 0, flexGrow: 1 }}>
            <MDTypography
              variant="caption"
              sx={{ color: "#6c757d", fontWeight: 600, fontSize: "11px", display: "block", mb: 0.5 }}
            >
              {label}
            </MDTypography>
            <MDTypography variant="h6" sx={{ mb: 0, fontWeight: 600, fontSize: "1.05rem" }}>
              {value}
            </MDTypography>
          </MDBox>
          <MDBox
            sx={{
              width: 36,
              height: 36,
              borderRadius: "50%",
              bgcolor: color,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            {icon}
          </MDBox>
        </MDBox>
        <MDTypography variant="caption" sx={{ mt: 1, display: "block", color: "#6c757d", fontSize: "11px" }}>
          {hint}
        </MDTypography>
      </CardContent>
    </Card>
  );
}

CompactStatCard.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node.isRequired,
  hint: PropTypes.node,
  color: PropTypes.string,
  icon: PropTypes.node,
  background: PropTypes.string,
};

export function DisputeKpiCard({ dispute }) {
  const data = normalizeDispute(dispute);
  return (
    <CompactStatCard
      label="Tổng dispute"
      value={formatNumber(data.total)}
      hint={`${formatPct(data.rate)} / ${formatNumber(data.totalOrders)} đơn`}
      color="#f59e0b"
      background="linear-gradient(to right, #fff4e6, #fffefb)"
      icon={<ReportProblemIcon sx={{ color: "#fff", fontSize: 18 }} />}
    />
  );
}

DisputeKpiCard.propTypes = {
  dispute: PropTypes.object,
};

export function DisputeCancelCard({ dispute }) {
  const data = normalizeDispute(dispute);
  return (
    <CompactStatCard
      label="Huỷ mới"
      value={formatNumber(data.cancelledNew.count)}
      hint={`${formatPct(data.cancelledNew.percent)} · Cancelled, basecost = 0`}
      color="#ef4444"
      background="linear-gradient(to right, #ffeeee, #fffcfc)"
      icon={<CancelOutlinedIcon sx={{ color: "#fff", fontSize: 18 }} />}
    />
  );
}

DisputeCancelCard.propTypes = {
  dispute: PropTypes.object,
};

export function DisputeRefundCard({ dispute }) {
  const data = normalizeDispute(dispute);
  return (
    <CompactStatCard
      label="Refund đã đi"
      value={formatNumber(data.refundedShipped.count)}
      hint={`${formatPct(data.refundedShipped.percent)} · Refunded, basecost > 0`}
      color="#ea580c"
      background="linear-gradient(to right, #fff7ed, #fffefb)"
      icon={<ReplayIcon sx={{ color: "#fff", fontSize: 18 }} />}
    />
  );
}

DisputeRefundCard.propTypes = {
  dispute: PropTypes.object,
};

function DisputeDashboard({ dispute }) {
  const data = useMemo(() => normalizeDispute(dispute), [dispute]);

  const storeRows = useMemo(() => {
    return Object.entries(data.byStore)
      .map(([store, row]) => ({
        store,
        orders: Number(row?.orders) || 0,
        dispute: Number(row?.dispute) || 0,
        rate: Number(row?.rate) || 0,
        byType: { ...emptyType(), ...(row?.byType || {}) },
      }))
      .sort((a, b) => b.rate - a.rate || b.dispute - a.dispute);
  }, [data.byStore]);

  const pieData = useMemo(
    () =>
      CASE_KEYS.map((key) => ({
        key,
        name: CASE_META[key].label,
        value: countOf(data.byType, key),
        percent: pctOf(data.byType, key),
        color: CASE_META[key].color,
      })).filter((row) => row.value > 0),
    [data.byType]
  );

  const mixData = useMemo(
    () => [
      { name: "Tổng đơn", value: data.totalOrders },
      { name: "Dispute", value: data.total },
    ],
    [data.total, data.totalOrders]
  );

  const gatewayData = useMemo(
    () =>
      GATEWAY_META.map(({ key, label }) => {
        const row = data.byGateway[key] || {};
        const point = { name: label };
        CASE_KEYS.forEach((type) => {
          point[type] = countOf(row, type);
        });
        return point;
      }),
    [data.byGateway]
  );

  return (
    <Grid container spacing={2} sx={{ mb: 2 }}>
      <Grid item xs={12} lg={8}>
        <Card sx={CARD_SX}>
          <CardContent sx={{ py: 1.5, px: 1.5, "&:last-child": { pb: 1.5 } }}>
            <MDTypography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.25 }}>
              Dispute theo store
            </MDTypography>
            {storeRows.length === 0 ? (
              <MDTypography variant="body2" color="text.secondary" textAlign="center" py={3}>
                Không có dữ liệu dispute
              </MDTypography>
            ) : (
              <div style={TABLE_WRAP}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ backgroundColor: "#f5f5f5" }}>
                      <th style={{ ...TH, textAlign: "left" }}>Store</th>
                      <th style={TH}>Tổng đơn</th>
                      <th style={TH}>Dispute</th>
                      <th style={TH}>Tỷ lệ</th>
                      {TYPE_TABLE_KEYS.map((key) => (
                        <th key={key} style={{ ...TH, color: CASE_META[key].color }}>
                          {CASE_META[key].label} %
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {storeRows.map((row) => (
                      <tr key={row.store}>
                        <td style={{ ...TD, textAlign: "left", fontWeight: 600 }}>{row.store}</td>
                        <td style={TD}>{formatNumber(row.orders)}</td>
                        <td style={TD}>{formatNumber(row.dispute)}</td>
                        <td style={{ ...TD, fontWeight: 700 }}>{formatPct(row.rate)}</td>
                        {TYPE_TABLE_KEYS.map((key) => (
                          <td key={key} style={TD}>
                            {formatPct(pctOf(row.byType, key))}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} md={6} lg={4}>
        <Card sx={CARD_SX}>
          <CardContent sx={{ py: 1.5, px: 1.5, "&:last-child": { pb: 1.5 } }}>
            <MDTypography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.25 }}>
              Chỉ số loại case
            </MDTypography>
            <div style={TABLE_WRAP}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ backgroundColor: "#f5f5f5" }}>
                    <th style={{ ...TH, textAlign: "left" }}>Loại</th>
                    <th style={TH}>Số case</th>
                    <th style={TH}>% dispute</th>
                  </tr>
                </thead>
                <tbody>
                  {TYPE_TABLE_KEYS.map((key) => (
                    <tr key={key}>
                      <td style={{ ...TD, textAlign: "left", fontWeight: 600, color: CASE_META[key].color }}>
                        {CASE_META[key].label}
                      </td>
                      <td style={TD}>{formatNumber(countOf(data.byType, key))}</td>
                      <td style={TD}>{formatPct(pctOf(data.byType, key))}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} md={6}>
        <Card sx={CARD_SX}>
          <CardContent sx={{ py: 1.5, px: 1.5, "&:last-child": { pb: 1.5 } }}>
            <MDTypography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.25 }}>
              Dispute / tổng đơn
            </MDTypography>
            <Grid container spacing={1}>
              <Grid item xs={12} sm={5}>
                <MDBox sx={{ height: 200 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={mixData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                      <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                      <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
                      <Tooltip />
                      <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                        <Cell fill="#487FFF" />
                        <Cell fill="#f59e0b" />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </MDBox>
              </Grid>
              <Grid item xs={12} sm={7}>
                <MDBox sx={{ height: 200 }}>
                  {pieData.length === 0 ? (
                    <MDBox display="flex" alignItems="center" justifyContent="center" height="100%">
                      <MDTypography variant="body2" color="text.secondary">
                        Chưa có case dispute
                      </MDTypography>
                    </MDBox>
                  ) : (
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={pieData}
                          dataKey="value"
                          nameKey="name"
                          cx="50%"
                          cy="50%"
                          innerRadius={36}
                          outerRadius={62}
                          paddingAngle={2}
                        >
                          {pieData.map((entry) => (
                            <Cell key={entry.key} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip
                          formatter={(value, name, item) => [
                            `${formatNumber(value)} (${formatPct(item?.payload?.percent)})`,
                            name,
                          ]}
                        />
                        <Legend wrapperStyle={{ fontSize: 12 }} />
                      </PieChart>
                    </ResponsiveContainer>
                  )}
                </MDBox>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} md={6}>
        <Card sx={CARD_SX}>
          <CardContent sx={{ py: 1.5, px: 1.5, "&:last-child": { pb: 1.5 } }}>
            <MDTypography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.25 }}>
              Dispute theo cổng thanh toán
            </MDTypography>
            <MDBox sx={{ height: 200 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={gatewayData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                  <Tooltip />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  {CASE_KEYS.map((key) => (
                    <Bar key={key} dataKey={key} name={CASE_META[key].label} stackId="case" fill={CASE_META[key].color} />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </MDBox>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
}

DisputeDashboard.propTypes = {
  dispute: PropTypes.object,
};

export default DisputeDashboard;
